import * as Pipeline from '../core/utils/pipeline';
import { AdsTooltipContent } from './components/ads-tooltip-content';
import { AdsTooltipErrors, OpenChangeEvent, OpenChangeHandler } from './ads-tooltip.types';
import { AdsTooltipTrigger } from './components/ads-tooltip-trigger';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Component, Element, Event, EventEmitter, Listen, Prop, State, VNode, Watch, h } from '@stencil/core';
import { Dictionary } from '../core/classes/Dictionary';
import { Position, Positions } from '../core/types/globalTypes';
import { ResizeObserver } from '@juggle/resize-observer';
import { Theme } from '../core/types/globalTypes';
import { generateId, showErrorMessage } from '../core/utils/components';
import { getIsElementRTL, isNullOrUndefined, isNullOrUndefinedOrEmpty, setFocusOnArea } from 'utils/index';

/**
 * Tooltips provide additional contextual information upon hover or focus of the question mark element. As default this will have ARIA label of "More information" but we recommend that you change the ARIA label attribute to be sufficiently descriptive to differentiate each tooltip on the page. For example "More information about CPUs"
 * @tag `<ads-tooltip>`
 * @slot content - Content rendered as rich text inside the component
 * @example <ads-tooltip text="Hello world"></ads-tooltip>
 */
@Component({
  tag: 'ads-tooltip',
  styleUrl: 'ads-tooltip.scss',
  shadow: true,
})
export class AdsTooltip implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  private timeout: NodeJS.Timeout;

  private ro: ResizeObserver;

  private contentId = generateId('ads:tooltip:content:');

  /**
   * @hidden
   */
  static AdsTooltip: any;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-tooltip'));
    this.component = 'ads-tooltip';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.handleBodyClick = this.handleBodyClick.bind(this);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.handleKeyUpEvent = this.handleKeyUpEvent.bind(this);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.handleFocusOutEvent = this.handleFocusOutEvent.bind(this);
    Dictionary.registerComponent(this);
  }

  @Element() private el: HTMLAdsTooltipElement;

  /**
   * The internal state of the modal. Dictated by the prop if one is provided
   * @hidden
   */
  @State() isOpenInternal = false;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The width the content box should take (optional)
   * @default "100%"
   */
  @Prop({ reflect: true }) contentWidth = '100%';

  /**
   * Defines whether or not the tooltip is open (optional)
   */
  @Prop({ reflect: true }) isOpen?: boolean;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Text to display as the aria-label (optional)
   * @default "More information"
   */
  @Prop({ reflect: true }) srLabel = Dictionary.get('More information', 'MORE_INFORMATION');

  /**
   * The position of the content box of the tooltip.
   * It can be one of 'top', 'left', and 'bottom'
   * @default "right"
   */
  @Prop({ reflect: true }) position: Position = Positions.RIGHT;

  /**
   * The text to be placed within the content box,
   * but any children placed within the tooltip tag will take precedence (optional)
   */
  @Prop({ reflect: true }) text?: string;

  /**
   * An element that will be used as a trigger for the tooltip
   * If not specified, the default trigger will be used
   */
  @Prop() customTriggerEl?: HTMLElement;

  /**
   * If true, the tooltip will also appear on trigger hover
   * @default false
   */
  @Prop() openOnHover = false;

  /**
   * The padding to be used between the trigger and the tooltip popup
   * @default 10
   */
  @Prop({ reflect: true }) triggerPadding = 10;

  /**
   * Callback function for the open state of the tooltip
   */
  @Prop() openChangeHandler?: OpenChangeHandler;

  /**
   * Emits 'openChange' custom event.<br/>
   * Usage: `document.querySelector('ads-tooltip').addEventListener('openChange', function(evt) {});`
   * @event
   */
  @Event() openChange: EventEmitter<OpenChangeEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-tooltip'));
  }

  /**
   * watch for changes on the prop
   * @param {boolean} newValue new isOpen value
   * @hidden
   */
  @Watch('isOpen')
  onIsOpenChanged(newValue: boolean): void {
    if (newValue === true || newValue === false) {
      this.isOpenInternal = newValue;
    }
  }

  /**
   * watch for changes on the prop
   * @param {Position} newValue the new position
   * @hidden
   */
  @Watch('position')
  onPositionChanged(newValue: Position): void {
    if (!Object.values(Positions).toString().includes(newValue)) {
      showErrorMessage(AdsTooltipErrors.INVALID_POSITION, this.el);
    }
  }

  /**
   * Watch for changes on custom trigger El. Add/remove event listener for "click"
   * @param {HTMLElement} newEl The new trigger element
   * @param {HTMLElement} oldEl The old trigger element
   * @hidden
   */
  @Watch('customTriggerEl')
  onCustomTriggerElChanged(newEl: HTMLElement, oldEl: HTMLElement): void {
    if (!isNullOrUndefined(newEl)) {
      this.addInteractionEventsToEl(newEl);
      this.addAccessibilityEventsToEl(newEl);
      if (this.openOnHover) {
        this.addHoverEventToElement(newEl);
      }
    }
    if (!isNullOrUndefined(oldEl)) {
      this.removeInteractionEventsToEl(oldEl);
      this.removeAccessibilityEventsToEl(oldEl);
      this.removeHoverEventToElement(oldEl);
    }
  }

  /**
   * Watch for changes in openOnHover and add/remove event listeners
   * @param {boolean} newValue The new value
   * @hidden
   */
  @Watch('openOnHover')
  onOpenOnHoverChange(newValue: boolean): void {
    if (!isNullOrUndefined(this.customTriggerEl)) {
      if (newValue) {
        this.addHoverEventToElement(this.customTriggerEl);
      } else {
        this.removeHoverEventToElement(this.customTriggerEl);
      }
    }
  }

  /**
   * Listen to global click events
   * @param {Event} evt The click event
   * @hidden
   */
  @Listen('click', { target: 'window' })
  handleBodyClick(evt: MouseEvent): void {
    if (this.isOpenInternal) {
      if (evt.target === this.el) {
        evt.stopPropagation();
      }
      if (this.el !== evt.target && !this.el.shadowRoot.contains(evt.target as Node) && !this.el.contains(evt.target as Node)) {
        this.closeTooltip();
      }
    }
  }

  /**
   * Listen to global keyUp events
   * @param {Event} evt The keyUp event
   * @hidden
   */
  @Listen('keyup', { capture: true, target: 'window' })
  handleKeyUpEvent(evt: KeyboardEvent): void {
    if (this.isOpenInternal) {
      switch (evt.code.toLowerCase()) {
        case 'escape': {
          this.closeTooltip();
          // Set focus to tooltip trigger
          const trigger: HTMLElement = this.getTriggerEl();
          if (!isNullOrUndefined(trigger)) {
            trigger.focus();
          }
          break;
        }
        default:
          break;
      }
    }
  }

  /**
   * Listen to global focusout events
   * @param {Event} evt The focusout event
   * @hidden
   */
  @Listen('focusout', { capture: true })
  handleFocusOutEvent(evt: FocusEvent): void {
    if (this.isOpenInternal) {
      if (evt.target === this.el) {
        evt.stopPropagation();
      }
      if (
        !isNullOrUndefined(evt.relatedTarget) &&
        evt.relatedTarget !== this.el &&
        !this.el.shadowRoot.contains(evt.relatedTarget as Node) &&
        !this.el.contains(evt.relatedTarget as Node) &&
        evt.relatedTarget !== this.customTriggerEl
      ) {
        this.closeTooltip();
      }
    }
  }

  /**
   * Returns the current trigger element
   * @returns {HTMLElement} The current custom trigger element
   */
  private getTriggerEl = (): HTMLElement => this.customTriggerEl || this.el.shadowRoot.querySelector('.c-tooltip__trigger');

  /**
   * Add click, focusout, keydown, keyup  event listeners and tabIndex to element
   * @param {HTMLElement} el The html element
   */
  private addInteractionEventsToEl = (el: HTMLElement): void => {
    el.addEventListener('click', this.toggleTooltip);
    el.addEventListener('focusout', this.handleFocusOutEvent);
    el.addEventListener('keydown', this.toggleTooltip);
    el.addEventListener('keyup', this.handleFocusOutEvent);
    el.style.cursor = 'pointer';
    el.tabIndex = 0;
  };

  /**
   * Remove click, focusout, keydown, keyup event listeners and tabIndex to element
   * @param {HTMLElement} el The html element
   */
  private removeInteractionEventsToEl = (el: HTMLElement): void => {
    el.removeEventListener('click', this.toggleTooltip);
    el.removeEventListener('focusout', this.handleFocusOutEvent);
    el.removeEventListener('keydown', this.toggleTooltip);
    el.removeEventListener('keyup', this.handleFocusOutEvent);
    el.style.removeProperty('cursor');
    el.tabIndex = -1;
  };

  /**
   * Add mouseenter and mouseleave event listeners to element
   * @param {HTMLElement} el The html element
   */
  private addHoverEventToElement = (el: HTMLElement): void => {
    el.addEventListener('mouseenter', this.toggleTooltip);
    el.addEventListener('mouseleave', this.toggleTooltip);
  };

  /**
   * Remove mouseenter and mouseleave event listeners from element
   * @param {HTMLElement} el The html element
   */
  private removeHoverEventToElement = (el: HTMLElement): void => {
    el.removeEventListener('mouseenter', this.toggleTooltip);
    el.removeEventListener('mouseleave', this.toggleTooltip);
  };

  /**
   * Add aria attributes for screen readers
   * @param {HTMLElement} el The html element
   */
  private addAccessibilityEventsToEl = (el: HTMLElement): void => {
    el.setAttribute('aria-describedby', this.el.getAttribute('id'));
    el.setAttribute('aria-controls', this.el.getAttribute('id'));
  };

  /**
   * Remove aria attributes for screen readers
   * @param {HTMLElement} el The html element
   */
  private removeAccessibilityEventsToEl = (el: HTMLElement): void => {
    el.removeAttribute('aria-describedby');
    el.removeAttribute('aria-controls');
  };

  /**
   * Open or close the tooltip content-box
   * @param {Event} evt The triggering event
   */
  private toggleTooltip = (evt: MouseEvent | KeyboardEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    switch (evt.type.toLowerCase()) {
      case 'mouseenter':
        if (!this.isOpenInternal) {
          this.onOpenChange(!this.isOpenInternal, evt);
        }
        break;
      case 'mouseleave':
        if (this.isOpenInternal) {
          this.onOpenChange(!this.isOpenInternal, evt);
        }
        break;
      case 'keydown':
        if ((evt as KeyboardEvent).key === 'Enter' || (evt as KeyboardEvent).key === ' ') {
          if (!this.isOpenInternal) {
            this.onOpenChange(!this.isOpenInternal, evt);
          }
        }
        break;
      case 'keyup':
        if ((evt as KeyboardEvent).key === 'Escape') {
          if (this.isOpenInternal) {
            this.onOpenChange(!this.isOpenInternal, evt);
          }
        }
        break;
      default:
        this.onOpenChange(!this.isOpenInternal, evt);
        break;
    }
  };

  private closeTooltip = (): void => {
    this.onOpenChange(false, null);
  };

  private onOpenChange = (newOpenState: boolean, evt?: Event): void => {
    this.openChange.emit({ newValue: newOpenState, evt });
    if (!isNullOrUndefined(this.openChangeHandler)) {
      this.openChangeHandler({ newValue: newOpenState, evt });
    }
    if (this.isOpen !== true && this.isOpen !== false) {
      this.isOpenInternal = newOpenState;
    }
  };

  private getPosition = (): Position => {
    return Object.values(Positions).toString().includes(this.position) ? this.position : Positions.RIGHT;
  };

  private positionTooltip = () => {
    if (this.isOpenInternal) {
      // position the content after rendering it.
      const content: HTMLElement = this.el.shadowRoot.querySelector('.c-tooltip__content');
      const position = this.getContentPosition(content, this.getPosition());
      content.style.setProperty('transform', `translate(${position[0]}px, ${position[1]}px)`);
      setFocusOnArea(content);
    }
  };

  /**
   * Get the coordinates to move an element with a given height and width
   * @param  {HTMLElement} content The content element
   * @param {Position} position The position option for the tooltip
   * @return {number[]} The coordinates of where the tooltip content should be placed
   */
  private getContentPosition = (content: HTMLElement, position: Position): number[] => {
    // Check whether the component should render in RTL mode
    const isRTL = getIsElementRTL(this.el);

    const trigger = this.getTriggerEl();
    const triggerBounds = trigger.getBoundingClientRect();
    const contentBounds = content.getBoundingClientRect();
    const tooltipBounds = content.parentElement.getBoundingClientRect();
    let moveContentBoxByX = 0;
    let moveContentBoxByY = 0;
    const pointerSize = 3; // from css
    const pointerDistanceIn = 10; // from css
    const triggerSizeX = triggerBounds.width;
    const triggerSizeY = triggerBounds.height;
    const contentSizeX = contentBounds.width;
    const contentSizeY = contentBounds.height;
    const triggerOriginX = triggerBounds.x;
    const triggerOriginY = triggerBounds.y;
    const tooltipOriginsX = tooltipBounds.x;
    const tooltipOriginsY = tooltipBounds.y;

    switch (position) {
      case Positions.TOP:
        moveContentBoxByX = triggerSizeX / 2 - (pointerDistanceIn + pointerSize);
        moveContentBoxByY = -(contentSizeY + this.triggerPadding + pointerSize);
        break;
      case Positions.RIGHT:
      default:
        moveContentBoxByX = triggerSizeX + this.triggerPadding + pointerSize;
        moveContentBoxByY = triggerSizeY / 2 - (pointerDistanceIn + pointerSize);
        break;
      case Positions.BOTTOM:
        moveContentBoxByX = triggerSizeX / 2 - (pointerDistanceIn + pointerSize);
        moveContentBoxByY = triggerSizeY + this.triggerPadding + pointerSize;
        break;
      case Positions.LEFT:
        moveContentBoxByX = -(contentSizeX + this.triggerPadding + pointerSize);
        moveContentBoxByY = triggerSizeY / 2 - (pointerDistanceIn + pointerSize);
        break;
    }

    // Basically we align the origins of the content and the trigger (so top left corners have the same exact position), and then we move it according to position
    return [
      triggerOriginX -
        tooltipOriginsX +
        moveContentBoxByX +
        contentSizeX * Number(isRTL) -
        triggerSizeX * Number(isRTL && trigger !== this.customTriggerEl),
      triggerOriginY - tooltipOriginsY + moveContentBoxByY,
    ];
  };

  protected componentWillLoad(): void {
    if (this.isOpen === true || this.isOpen === false) {
      this.isOpenInternal = this.isOpen;
    }
    if (!isNullOrUndefined(this.customTriggerEl)) {
      this.addInteractionEventsToEl(this.customTriggerEl);
      this.addAccessibilityEventsToEl(this.customTriggerEl);
      if (this.openOnHover) {
        this.addHoverEventToElement(this.customTriggerEl);
      }
    }
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
    this.ro = new ResizeObserver(() => {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.positionTooltip();
      }, 200);
    });
    this.ro?.observe(document.body);
  }

  protected disconnectedCallback(): void {
    if (!isNullOrUndefined(this.customTriggerEl)) {
      this.removeInteractionEventsToEl(this.customTriggerEl);
      this.removeAccessibilityEventsToEl(this.customTriggerEl);
      this.removeHoverEventToElement(this.customTriggerEl);
    }

    this.ro?.disconnect();
    Pipeline.removeComponent(this.el);
  }

  protected componentDidRender(): void {
    this.positionTooltip();
  }

  protected render(): VNode {
    return (
      <div class="c-tooltip">
        {isNullOrUndefined(this.customTriggerEl) ? (
          <AdsTooltipTrigger
            tooltipIsActive={this.isOpenInternal}
            describedById={this.contentId}
            label={this.srLabel}
            toggleEventHandler={this.toggleTooltip}
            openTooltipOnInteraction={this.openOnHover}
          />
        ) : null}
        <AdsTooltipContent
          content={!isNullOrUndefinedOrEmpty(this.text) ? this.text : ((<slot name="content" />) as HTMLSlotElement)}
          isActive={this.isOpenInternal}
          id={this.contentId}
          position={this.getPosition()}
          width={this.contentWidth}
        />
      </div>
    ) as VNode;
  }
}
